import React, { useEffect, useRef, useState } from "react";
import AppTable from "../../../common/AppTable/AppTable";
import UploadViaCsvBtn from "../../../common/AppSpecificConpnents/UploadViaCsvBtn";
import ExportCsvBtn from "../../../common/AppSpecificConpnents/ExportCsvBtn";
import AddBtn from "../../../common/AppSpecificConpnents/AddBtn";
import { useDispatch, useSelector } from "react-redux";
import {
  createProject,
  deleteProject,
  getAllProjects,
  updateProject,
} from "../../../redux/projectIdSlice";
import {
  getAuditTrailData,
  GetAuditTrailForImport,
} from "../../../util/getAuditTrailData";
import { showSnackbar } from "../../../redux/snackbarSlice";
import { scrollToTop, uploadJsonToS3 } from "../../../util/AppUtils";
import {
  exportCSV as exportProjectCSV,
  uploadCSV as uploadCSVForProject,
} from "../../../redux/projectIdSlice";
import DeleteConfirmation from "../../../common/Confirmations/DeleteConfirmation";
import ImportCsvSuccessPopup from "../../../common/ImportCsvSuccessPopup";
import Loader from "../../../common/Loader";
import { TableActionGroup } from "../../../common/Buttons";

const ProjectIdTable = ({
  handleSelectProject,
  useTableInAuditTrail = false,
  auditTrailRow,
}) => {
  const columns = [
    {
      headName: "Project ID",
      field: "product_code",
      sort: true,
      filter: true,
      required: true,
      width: "45%",
    },
    {
      headName: "Description",
      field: "description",
      sort: true,
      filter: true,
      width: "45%",
    },
    { headName: "Action", field: "action", isAction: true },
  ];
  const [showLoader, setShowLoader] = useState(false);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const tableContainerRef = useRef();

  const { projects, pagination } = useSelector((state) => state.projectId);
  const dispatch = useDispatch();
  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const [newRow, setNewRow] = useState(null);

  const [editIndex, setEditIndex] = useState(null);
  const [editedProjectId, setEditedProjectId] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [importedInfo, setImportData] = useState(null);

  const fileInputRef = useRef();
  const handleProjectFileChange = async (e, actionType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset the file input to allow same file upload next time
    e.target.value = "";

    const auditTrailData = await GetAuditTrailForImport();

    try {
      setShowLoader(true);
      const response = await dispatch(
        uploadCSVForProject({ csvFile: file, auditTrailData })
      );
      setShowLoader(false);
      if(response?.payload?.success){
        snackbar(
          response.payload.message,
          "success"
        );
      }
      if (response?.payload?.success) {
        dispatch(getAllProjects({ searchQuery }));
      }
      setImportData(response?.payload);
    } catch (error) {
      snackbar(
        "Error importing CSV: " + (error.message || "Unknown error"),
        "error"
      );
    }
  };

  useEffect(() => {
    if (!useTableInAuditTrail) dispatch(getAllProjects({ searchQuery }));
  }, [searchQuery, dispatch, useTableInAuditTrail]);

  const handleSubmit = async (e) => {
    const { product_code, description } = newRow;
    setShowLoader(true);
    if (product_code?.trim().length === 0) {
      snackbar("Project ID is mandatory.", "error");
      return;
    }

    const audit_trail_data = await getAuditTrailData({
      action: "CREATE_PROJECT_ID",
      comment: `Created Project ID: ${product_code?.trim()}`,
      version_content: {
        action_to_show: `Created Project ID: ${product_code?.trim()}`,
        product_code: product_code?.trim(),
        description: description?.trim(),
      },
    });

    const new_content_snapshot = await uploadJsonToS3(
      {
        action_to_show: `Created Project ID: ${product_code?.trim()}`,
        product_code: product_code?.trim(),
        description: description?.trim(),
      },
      "project_id_created",
      "CREATE",
      true
    );

    audit_trail_data.new_content_snapshot = new_content_snapshot;
    audit_trail_data.version_content = { action_to_show: audit_trail_data.version_content.action_to_show}

    const finalData = {
      product_code: product_code?.trim(),
      description: description?.trim(),
      audit_trail_data,
    };

    try {
      
      const resp = await dispatch(createProject(finalData));
      setShowLoader(false);
      const { message, error, success } = resp.payload;
      snackbar(success ? message : error, success ? "success" : "error");
      if (success) {
        dispatch(getAllProjects({ searchQuery }));
        setNewRow(null);
      }
    } catch (error) {
      snackbar("Error creating project: " + error, "error");
    }
  };

  const handleUpdate = async (project) => {
    try {
      const prevData = projects.find(
        (p) => p.project_id === project.project_id
      );

      setShowLoader(true);

      const audit_trail_data = await getAuditTrailData({
        action: "UPDATE_PROJECT_ID",
        comment: `Project ID ${project.product_code} has been updated. `,
        parent_version: prevData.version_id_ref,
        version_content: {
          product_code: editedProjectId,
          description: editedDescription,
          action_to_show: `Updated Project ID: ${project.product_code}`,
        },
      });

      const new_content_snapshot = await uploadJsonToS3(
        {
          action_to_show: `Updated Project ID: ${project.product_code}`,
          product_code: editedProjectId,
          description: editedDescription,
        },
        "project_id_updated",
        "UPDATE",
        true
      );

      audit_trail_data.new_content_snapshot = new_content_snapshot;
      audit_trail_data.version_content = { action_to_show: audit_trail_data.version_content.action_to_show}

      const finalData = {
        product_code: editedProjectId,
        description: editedDescription,
        audit_trail_data: audit_trail_data,
      };
      
      await dispatch(
        updateProject({ id: project.project_id, data: finalData })
      );
      setShowLoader(false);
      dispatch(getAllProjects({ searchQuery }));
      setShowLoader(false);
      snackbar("Project ID updated successfully!", "success");
    } catch (error) {
      snackbar("Error updating Project ID: " + error, "error");
    } finally {
      setEditIndex(null);
      setEditedProjectId("");
      setEditedDescription("");
    }
  };

  const handleProjectExportCSV = async () => {
    setShowLoader(true);
    try {
      const response = await dispatch(exportProjectCSV());
      setShowLoader(false);
      snackbar("Project IDs table exported successfully.", "success");
    } catch (error) {
      snackbar("Error exporting CSV: " + error, "error");
    }
  };
  const handleDelete = (id) => {
    DeleteConfirmation(() => {
      setShowLoader(true);
      dispatch(deleteProject(id)).then(() => {
        setShowLoader(false);
        snackbar("Project ID deleted successfully", "success");
        dispatch(getAllProjects({ searchQuery }));
      });
    }, "Are you sure you want to delete this Project ID?");
  };
  return (
    <>
      <AppTable
        ref={tableContainerRef}
        columnsData={
          useTableInAuditTrail
            ? columns
                .map((d) => ({ ...d, sort: false }))
                .filter((a) => a.field !== "action")
            : columns
        }
        usingInPopup={true}
        showSearchInput={useTableInAuditTrail ? false : true}
        paginate={useTableInAuditTrail ? false : true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination,
          paginationPosition: {
            position: "center",
            leftContent: (
              <>
                <AddBtn
                  onClick={() => {
                    // Check if a row is already being edited
                    if (editIndex !== null) {
                      snackbar(
                        "Please save or cancel the current row before adding or editing another one.",
                        "error"
                      );
                      return;
                    }

                    // Check if a new row is already being added
                    if (newRow !== null) {
                      snackbar(
                        "Please save or cancel the current row before adding or editing another one.",
                        "error"
                      );
                      return;
                    }

                    setNewRow({ product_code: "", description: "" });
                    scrollToTop(tableContainerRef);
                  }}
                />
              </>
            ),
            rightContent: (
              <>
                <UploadViaCsvBtn
                  fileInputRef={fileInputRef}
                  handleFileChange={handleProjectFileChange}
                />{" "}
                &nbsp;
                <ExportCsvBtn handleExportCSV={handleProjectExportCSV} />
              </>
            ),
          },
          paginationShadow: true,
        }}
        onFilterSortHandler={setSearchQuery}
      >
        {!useTableInAuditTrail && (
          <>
            {newRow && (
              <tr>
                <td>
                  <input
                    type="text"
                    style={{
                      height: "25px",
                      fontSize: "13px",
                      width: "100%",
                    }}
                    value={newRow?.product_code}
                    onChange={(e) =>
                      setNewRow((prev) => ({
                        ...prev,
                        product_code: e.target.value,
                      }))
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    style={{
                      height: "25px",
                      fontSize: "13px",
                      width: "100%",
                    }}
                    value={newRow?.description}
                    onChange={(e) =>
                      setNewRow((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </td>
                <td>
                  <TableActionGroup
                    mode="editing"
                    onSave={handleSubmit}
                    onCancel={() => setNewRow(null)}
                  />
                </td>
              </tr>
            )}
            {projects.map((project, index) => (
              <tr key={project.project_id}>
                <td>
                  {editIndex === index ? (
                    <input
                      type="text"
                      style={{
                        height: "25px",
                        fontSize: "13px",
                        width: "100%",
                      }}
                      value={editedProjectId}
                      disabled
                    />
                  ) : (
                    project.product_code
                  )}
                </td>
                <td>
                  {editIndex === index ? (
                    <input
                      type="text"
                      style={{ height: "25px", fontSize: "13px" }}
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                    />
                  ) : (
                    project.description
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  {editIndex === index ? (
                    <TableActionGroup
                      mode="editing"
                      onSave={() => handleUpdate(project)}
                      onCancel={() => {
                        setEditIndex(null);
                        setEditedProjectId("");
                        setEditedDescription("");
                      }}
                    />
                  ) : (
                    <TableActionGroup
                      mode="view"
                      showEdit={true}
                      showDelete={!handleSelectProject}
                      showSelect={!!handleSelectProject}
                      disableEdit={editIndex !== null || newRow !== null}
                      disableDelete={
                        project.has_modules ||
                        editIndex !== null ||
                        newRow !== null
                      }
                      disableSelect={editIndex !== null || newRow !== null}
                      onEdit={() => {
                        // Check if a row is already being edited
                        if (editIndex !== null) {
                          snackbar(
                            "Please save or cancel the current row before adding or editing another one.",
                            "error"
                          );
                          return;
                        }

                        // Check if a new row is already being added
                        if (newRow !== null) {
                          snackbar(
                            "Please save or cancel the current row before adding or editing another one.",
                            "error"
                          );
                          return;
                        }
                        setEditIndex(index);
                        setEditedProjectId(project.product_code);
                        setEditedDescription(project.description);
                      }}
                      onDelete={() => {
                        // Check if a row is already being edited
                        if (editIndex !== null) {
                          snackbar(
                            "Please save or cancel the current row before adding or editing another one.",
                            "error"
                          );
                          return;
                        }

                        // Check if a new row is already being added
                        if (newRow !== null) {
                          snackbar(
                            "Please save or cancel the current row before adding or editing another one.",
                            "error"
                          );
                          return;
                        }
                        handleDelete(project.project_id);
                      }}
                      onSelect={() => handleSelectProject(project)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </>
        )}

        {useTableInAuditTrail && <>{auditTrailRow}</>}
      </AppTable>

      {importedInfo && (
        <ImportCsvSuccessPopup
          importedInfo={importedInfo}
          setNullInportData={setImportData}
        />
      )}

      {showLoader && <Loader />}
    </>
  );
};

export default ProjectIdTable;
